/** @jsx jsx */

import { Button } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  useAvroEvent,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import { getAppUrl } from 'app/developer-v3/utils';

import * as CTAs from './CTAs';
import { getLockedHints, getReadOnlyReasons } from './utils';

type Props = {
  appId: ID;
  hasAuthentication: boolean;
  hasTriggers: boolean;
  hasValidated: boolean;
  hasValidationErrors: boolean;
  ordinal: number;
  userCount: number;
};

function CTAPublish({
  appId,
  hasAuthentication,
  hasTriggers,
  hasValidated,
  hasValidationErrors,
  ordinal,
  userCount,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const {
    isUserCountLimitExceeded,
    productionOrUserCountLimitExceededHint,
  } = getReadOnlyReasons(currentVersionReadOnlyReason, userCount);
  const lockedHints = getLockedHints(userCount);

  const needAuthHint = hasAuthentication ? undefined : lockedHints.needAuth;
  const needTriggerHint = hasTriggers ? undefined : lockedHints.needTrigger;
  const needValidationHint = hasValidated
    ? undefined
    : lockedHints.needValidation;
  const hasValidationErrorHint = hasValidationErrors
    ? lockedHints.validationError
    : undefined;

  const hint =
    productionOrUserCountLimitExceededHint ||
    needAuthHint ||
    needTriggerHint ||
    needValidationHint ||
    hasValidationErrorHint ||
    lockedHints.needAction;

  const appUrl = getAppUrl(appId);

  return (
    <CTAs.Lockable
      action={
        <Button
          aria-label="Publish"
          href={`${appUrl}/publishing`}
          onClick={() => {
            emitPartnerUserInteractionEvent({
              eventAction: 'click',
              interactionGoal: 'Start publishing process',
              interactionName: 'integration_home_button_clicked',
              interactionRawText: 'Publish',
              optionSelected: 'publish',
            });
          }}
        >
          Publish
        </Button>
      }
      description="When you’re ready, let our review team know you want to take your integration public. We’ll respond with feedback and a timeline for a marketing launch."
      hint={hint}
      icon="zapierLogo"
      isActive={hasAuthentication && hasValidated}
      isLocked={
        isUserCountLimitExceeded || !hasValidated || hasValidationErrors
      }
      learnMoreUrl={`${BASE_DOC_URL_VB}/publish/public-integration#3-publish-your-integration-to-the-app-directory`}
      ordinal={ordinal}
      title="Publish"
    />
  );
}

export default CTAPublish;
